import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import BgOrnaments from "@/components/vu/BgOrnaments";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const tiers = [
    {
        id: "foundation",
        name: "Foundation",
        badge: "STARTER",
        desc: "For businesses that need a strong website and local presence first.",
        price: "$499",
        priceSuffix: "one-time",
        cadence: "then $300/year for hosting & support after year one",
        features: [
            "Custom website design & build",
            "Google Business Profile setup",
            "Local SEO basics",
            "1 year hosting & support included",
        ],
        cta: "Get Started",
        pop: false, ai: false,
    },
    {
        id: "elite-ai",
        name: "Elite + AI",
        badge: "MOST POPULAR",
        desc: "Every channel plus a 24/7 AI voice agent answering, qualifying and booking every lead.",
        price: "$659",
        originalPrice: "$899",
        priceSuffix: "setup",
        cadence: "then $245/month · includes AI phone stack",
        features: [
            "Everything in Growth",
            "AI voice agent (inbound + outbound)",
            "SMS & email follow-ups automated",
            "Call recording, transcripts & sentiment",
            "CRM sync — Salesforce, HubSpot, Sheets",
        ],
        cta: "Get Started",
        pop: true, ai: true,
    },
    {
        id: "growth",
        name: "Growth",
        badge: "FULL MARKETING",
        desc: "For businesses ready to actively run ads and grow their list.",
        price: "$699",
        priceSuffix: "setup",
        cadence: "then $200/month · ad spend billed separately",
        features: [
            "Everything in Foundation",
            "Google & Facebook Ads management",
            "Monthly email campaign",
            "Dedicated account manager",
            "Monthly strategy call",
        ],
        cta: "Get Started",
        pop: false, ai: false,
    },
];

export default function Pricing() {
    const [loadingTier, setLoadingTier] = useState(null);

    const goToCheckout = async (tierId) => {
        setLoadingTier(tierId);
        if (window.gtag) {
            window.gtag('event', 'begin_checkout', {
                currency: 'USD',
                items: [{ item_id: tierId, item_name: tierId }],
            });
        }

        if (window.fbq) {
            window.fbq('track', 'InitiateCheckout', { content_ids: [tierId] });
        }
        
        try {
            const { data } = await axios.post(`${API}/checkout/create-session`, { tier: tierId });
            window.location.href = data.url;
        } catch (err) {
            console.error(err);
            toast.error("Couldn't start checkout. Please try again.");
            setLoadingTier(null);
        }
    };

    return (
        <section id="pricing" data-testid="pricing-section" className="py-[110px] relative border-t border-[#1F3A2B] overflow-hidden">
            <BgOrnaments variant="default" />
            <div className="max-w-[1180px] mx-auto px-8">
                <div className="text-center max-w-[600px] mx-auto mb-14">
                    <div className="inline-flex items-center gap-2 px-3.5 py-1.5 border border-[#2A4A38] bg-[#152A20]/60 backdrop-blur rounded-full text-[11.5px] text-[#B8CFC0] mb-4">
                        <span className="w-[7px] h-[7px] rounded-full bg-[#DFFF3D]" /> Flexible Plans
                    </div>
                    <h2 className="font-display text-[2.4rem] mb-3.5 text-[#F5F0E8]">Pricing that <span className="italic-serif text-[#DFFF3D]">fits your growth.</span></h2>
                    <p className="text-[#7A9088] text-[15.5px]">Honest pricing. No lock-in. Cancel monthly plans with 30 days' notice.</p>
                </div>
                <div className="grid md:grid-cols-3 gap-5">
                    {tiers.map((t, i) => (
                        <motion.div key={t.name} initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-10%" }} transition={{ duration: 0.6, delay: i * 0.08 }} data-testid={`pricing-card-${t.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className={`rounded-[20px] p-8 flex flex-col border relative tilt-lift will-change-transform ${t.pop ? "bg-gradient-to-b from-[#DFFF3D] to-[#B8D622] border-[#DFFF3D] text-[#0E1F16] warm-glow -translate-y-2" : "bg-[#152A20] border-[#1F3A2B] soft-shadow text-white"}`}>
                            {t.pop && <div data-testid="pricing-most-popular" className="absolute -top-3 left-8 mono text-[10px] px-2.5 py-1 rounded-full bg-[#0E1F16] text-[#DFFF3D] font-bold">{t.badge}</div>}
                            {!t.pop && <div className={`self-start mono text-[10.5px] px-2.5 py-1 rounded-full mb-4 ${t.ai ? "bg-[#DFFF3D]/15 text-[#DFFF3D] border border-[#DFFF3D]/30" : "bg-[#0E1F16]/40 text-[#A6BDB0]"}`}>{t.badge}</div>}
                            <h3 className={`font-bold text-[19px] mb-2 mt-1 ${t.pop ? "text-[#0E1F16]" : "text-[#F5F0E8]"}`}>{t.name}</h3>
                            <div className={`text-[13px] mb-5 leading-[1.5] ${t.pop ? "text-[#0E1F16]/75" : "text-[#7A9088]"}`}>{t.desc}</div>
                            <div className={`font-display text-[2.6rem] leading-none mb-1 flex items-baseline gap-2.5 flex-wrap ${t.pop ? "text-[#0E1F16]" : "text-[#F5F0E8]"}`}>
                                {t.originalPrice && (
                                    <span className={`text-[1.5rem] font-medium line-through opacity-60 ${t.pop ? "text-[#0E1F16]" : "text-[#7A9088]"}`}>{t.originalPrice}</span>
                                )}
                                {t.price}
                                <span className={`text-sm font-medium ml-1.5 ${t.pop ? "text-[#0E1F16]/70" : "text-[#7A9088]"}`}>{t.priceSuffix}</span>
                            </div>
                            <div className={`text-xs mb-5 mt-2 ${t.pop ? "text-[#0E1F16]/70" : "text-[#A6BDB0]"}`}>{t.cadence}</div>
                            <ul className="mb-7 flex-1">
                                {t.features.map((f) => (
                                    <li key={f} className={`flex gap-2.5 text-[13.5px] py-2 ${t.pop ? "text-[#0E1F16]" : "text-[#B8CFC0]"}`}>
                                        <i className={`fa-solid fa-check text-[11px] mt-1 ${t.pop ? "text-[#0E1F16]" : "text-[#DFFF3D]"}`} />{f}
                                    </li>
                                ))}
                            </ul>
                            {t.ai && (
                                <div className={`mb-4 -mt-2 mono text-[10.5px] flex items-center gap-2 flex-wrap ${t.pop ? "text-[#0E1F16]/75" : "text-[#7A9088]"}`}>
                                    <i className={`fa-solid fa-satellite-dish ${t.pop ? "text-[#0E1F16]" : "text-[#DFFF3D]"}`} />
                                    <span>AI phone stack powered by</span>
                                    <a href="https://orbitforceai.com/" target="_blank" rel="noopener noreferrer" className={`mono hover:underline ${t.pop ? "text-[#0E1F16] font-bold" : "text-[#DFFF3D]"}`}>Orbit Force AI</a>
                                </div>
                            )}
                            <button
                                onClick={() => goToCheckout(t.id)}
                                disabled={loadingTier === t.id}
                                data-testid={`pricing-cta-${t.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                className={`vu-btn inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-semibold whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed ${t.pop ? "bg-[#0E1F16] text-[#DFFF3D] hover:bg-[#050D08]" : t.ai ? "bg-[#DFFF3D] text-[#0E1F16] hover:bg-[#C7E82F]" : "border border-white/20 text-[#F5F0E8] hover:bg-white/5"}`}
                            >
                                {loadingTier === t.id ? "Redirecting…" : t.cta} <i className="fa-solid fa-arrow-right text-xs" />
                            </button>
                        </motion.div>
                    ))}
                </div>
                <p className="text-center mt-10 mono text-[10.5px] text-[#7A9088] flex items-center justify-center gap-2 flex-wrap">
                    <i className="fa-solid fa-handshake text-[#DFFF3D]" />
                    <span>In partnership with</span>
                    <a href="https://orbitforceai.com/" target="_blank" rel="noopener noreferrer" className="mono text-[#DFFF3D] hover:underline">Orbit Force AI</a>
                    <span>— enterprise-grade AI voice agents for growing businesses.</span>
                </p>
            </div>
        </section>
    );
}